"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePowerUsageEnergySavings = void 0;
const react_1 = require("react");
const queries_1 = require("../../graphql/queries");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const DEFAULT_DAYS = 30;
const DEFAULT_ENERGY_RATE_PER_KWH = 0.12; // $0.12 per kWh - average US rate
/**
 * Transforms SensorUsageRollup data into DailyEnergyCost format
 * Handles hourly rollup data by aggregating into daily totals
 */
const transformPowerDataToEnergyCosts = (powerData, energyRatePerKwh) => {
    // Group data by date to handle hourly rollups
    const dataByDate = {};
    // First pass: group all hourly data by date
    powerData.forEach((hourlyData) => {
        const date = hourlyData.date;
        if (!dataByDate[date]) {
            dataByDate[date] = [];
        }
        dataByDate[date].push(hourlyData);
    });
    // Second pass: calculate daily totals from hourly data
    return Object.entries(dataByDate).map(([date, hourlyRecords]) => {
        // Calculate the total energy for each hour and sum them up
        let dailyEnergyKwh = 0;
        hourlyRecords.forEach((hourData) => {
            // Access the averageMainCt1/2 and averageVt1/2 values from either direct properties
            // or from the powerUsageRollup object if it exists
            const mainCt1 = hourData.averageMainCt1 || hourData.powerUsageRollup?.averageMainCt1 || 0;
            const mainCt2 = hourData.averageMainCt2 || hourData.powerUsageRollup?.averageMainCt2 || 0;
            const vt1 = hourData.averageVt1 || hourData.powerUsageRollup?.averageVt1 || 0;
            const vt2 = hourData.averageVt2 || hourData.powerUsageRollup?.averageVt2 || 0;
            // Calculate power in watts (current * voltage)
            const mainCt1Power = mainCt1 * vt1; // Watts
            const mainCt2Power = mainCt2 * vt2; // Watts
            // Convert watts to kWh for this hour (divide by 1000 for kW)
            // Since these are hourly averages, we're calculating kWh directly (kW * 1 hour)
            const hourlyEnergyKwh = (mainCt1Power + mainCt2Power) / 1000;
            // Add this hour's energy to the daily total
            dailyEnergyKwh += hourlyEnergyKwh;
        });
        // Calculate cost for the day
        const costForDay = dailyEnergyKwh * energyRatePerKwh;
        return {
            label: date,
            value: Number(costForDay.toFixed(2)),
        };
    });
};
/**
 * Hook that provides energy savings data based on SensorUsageRollup records
 * @param params - Parameters for fetching and calculating energy savings
 * @returns Energy savings data with cost calculations
 */
const usePowerUsageEnergySavings = ({ sensorId, days = DEFAULT_DAYS, hubConfig, enabled = true, }) => {
    const [data, setData] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const effectiveEnergyRate = (0, react_1.useMemo)(() => {
        // Only try to access the Electrical.PriceModelValues if hubConfig exists
        const configRate = hubConfig?.Electrical?.PriceModelValues?.rate_per_kWh;
        return configRate ?? DEFAULT_ENERGY_RATE_PER_KWH;
    }, [hubConfig ? JSON.stringify(hubConfig) : null]);
    // Calculate date range
    const endDate = (0, react_1.useMemo)(() => new Date().toISOString().split('T')[0], // Today in YYYY-MM-DD
    []);
    const startDate = (0, react_1.useMemo)(() => {
        const date = new Date();
        date.setDate(date.getDate() - days);
        return date.toISOString().split('T')[0]; // N days ago in YYYY-MM-DD
    }, [days]);
    const fetchPowerUsageData = async () => {
        if (!enabled || !sensorId) {
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const allData = [];
            let nextToken;
            do {
                const result = await amplifyBootstrap_1.API.graphql({
                    query: queries_1.getSensorUsageRollup,
                    variables: {
                        sensorId,
                        startDate,
                        endDate,
                        nextToken,
                    },
                });
                const response = result?.data?.getSensorUsageRollup;
                const pageData = response?.items;
                if (pageData?.length > 0) {
                    allData.push(...pageData);
                }
                nextToken = response?.nextToken;
            } while (nextToken);
            if (allData.length > 0) {
                const historicalData = transformPowerDataToEnergyCosts(allData, effectiveEnergyRate);
                setData(historicalData);
            }
            else {
                setData([]);
            }
        }
        catch (err) {
            console.error('Error fetching power usage data for energy savings:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch power usage data for energy savings');
            setData(null);
        }
        finally {
            setLoading(false);
        }
    };
    const refetch = async () => {
        await fetchPowerUsageData();
    };
    // Store previous sensorId to prevent unnecessary refetches
    const prevSensorIdRef = (0, react_1.useRef)(null);
    const prevEnergyRateRef = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        // Only fetch new data if key values have changed
        const sensorChanged = prevSensorIdRef.current !== sensorId;
        const rateChanged = prevEnergyRateRef.current !== effectiveEnergyRate;
        if (sensorChanged || rateChanged || !data) {
            prevSensorIdRef.current = sensorId || null;
            prevEnergyRateRef.current = effectiveEnergyRate;
            fetchPowerUsageData();
        }
    }, [sensorId, startDate, endDate, enabled, effectiveEnergyRate]);
    return {
        data,
        loading,
        error,
        refetch,
    };
};
exports.usePowerUsageEnergySavings = usePowerUsageEnergySavings;
